import React, { useEffect, useState, useRef, useCallback } from "react";
import { connect } from "react-redux";
import axios from "axios";
import data from "../../../../assets/apiRoutes.json";
import { WMSInventoryUOMFlags } from "../../../../const/constants";
import { GridLayout } from "../../../../common/GridLayout";
import {
    Column,
    DataGrid,
    GroupPanel,
    Grouping,
    FilterRow,
    FilterPanel,
    HeaderFilter,
    SearchPanel,
    Sorting,
    Paging,
    Selection,
    Toolbar,
    Item,
    Export,
    Pager,
    Lookup,
    ColumnChooser,
    ColumnChooserSearch,
    StateStoring,
} from "devextreme-react/data-grid";

const numberFormat = { type: "fixedPoint", precision: 2 };

/**
 * Reusable WM transaction-detail grid backed by
 * wm/TransactionDetail/GetTransactionDetails?transactionRowId=<header rowid>.
 * Pass the transaction header rowid via `transactionId`.
 */
const CustomerTransactionDetails = (props) => {
    const { transactionId, gridKey } = props;
    const accessToken = props.oidc.user.access_token;
    const dataGrid = useRef(null);
    const [rows, setRows] = useState([]);
    const [currentLayout, setCurrentLayout] = useState("Default");

    const allowedPageSizes = [10, 20, 100, 500, "all"];
    const stateKey = gridKey;
    const loadState = useCallback(() => GridLayout.sendStorageRequest(stateKey, "GET", accessToken, null, currentLayout), [stateKey, currentLayout]);
    const saveState = useCallback((state) => GridLayout.sendStorageRequest(stateKey, "POST", accessToken, state, currentLayout), [stateKey, currentLayout]);

    useEffect(() => {
        if (!transactionId) {
            setRows([]);
            return;
        }
        axios.get(`${window._env_.REACT_APP_APIS}/${data.Transaction.GetTransactionDetails}?transactionRowId=${transactionId}`, {
            headers: {
                access_token: accessToken,
                Authorization: `Bearer ${accessToken}`,
            },
        }).then((res) => {
            setRows(Array.isArray(res.data) ? res.data : []);
        }).catch((err) => {
            console.error("Failed to load transaction details:", err);
            setRows([]);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [transactionId]);

    return (
        <DataGrid
            onExporting={(e) => {
                if (window.handleDevExtremeDataGridExport) {
                    window.handleDevExtremeDataGridExport(e);
                }
            }}
            ref={dataGrid}
            dataSource={rows}
            keyExpr="rowid"
            allowColumnReordering={true}
            allowColumnResizing={true}
            showBorders={true}
            hoverStateEnabled={true}
            columnAutoWidth={true}
        >
            <ColumnChooser enabled={true} title="Choose Column" width={290} height={290} sortOrder="asc">
                <ColumnChooserSearch enabled={true} editorOptions={{ placeholder: 'Search column' }} />
            </ColumnChooser>
            <Export enabled={true} allowExportSelectedData={true} />
            <Selection mode="single" />
            <Sorting visible={true} mode="multiple" />
            <Paging defaultPageSize={10} />
            <Pager visible={true} showPageSizeSelector={true} allowedPageSizes={allowedPageSizes} showNavigationButtons={true} />
            <FilterRow visible={true} />
            <FilterPanel visible={true} />
            <HeaderFilter visible={true} />
            <SearchPanel visible={true} width={240} placeholder="Search..." />
            <GroupPanel visible={true} />
            <Grouping autoExpandAll={false} />
            <StateStoring enabled={true} type="custom" customLoad={loadState} storageKey={stateKey} customSave={saveState} />

            {/* Default-visible columns (mirrors the AI8 detail layout) */}
            <Column dataField="itemCode" caption="Item" />
            <Column dataField="lotCode" caption="Lot" />
            <Column dataField="inventoryUomFlag" caption="Trans Unit">
                <Lookup
                    dataSource={WMSInventoryUOMFlags}
                    displayExpr="Text"
                    valueExpr="ID"
                />
            </Column>
            <Column dataField="quantityordered" caption="Order" dataType="number" format={numberFormat} />
            <Column dataField="actualQuantity" caption="Actual" dataType="number" format={numberFormat} />
            <Column dataField="unitOfMeasure" caption="UOM" />
            <Column dataField="inventoryUnits" caption="Inventory" dataType="number" format={numberFormat} />
            <Column dataField="inventoryUnitUOM" caption="UOM" />
            <Column dataField="amount" caption="Amount" dataType="number" format="$ #,##0.00" />
            <Column dataField="code1" caption="Code 1" />
            <Column dataField="code2" caption="Code 2" />

            {/* Column-chooser (hidden by default) */}
            <Column dataField="code3" caption="Code 3" visible={false} />
            <Column dataField="code4" caption="Code 4" visible={false} />
            <Column dataField="code5" caption="Code 5" visible={false} />
            <Column dataField="companyId" caption="Company ID" visible={false} />
            <Column dataField="creationUserId" caption="Created By" visible={false} />
            <Column dataField="creationDateTime" caption="Creation Date" dataType="date" visible={false} />
            <Column dataField="date1" caption="Date 1" dataType="date" visible={false} />
            <Column dataField="date2" caption="Date 2" dataType="date" visible={false} />
            <Column dataField="date3" caption="Date 3" dataType="date" visible={false} />
            <Column dataField="date4" caption="Date 4" dataType="date" visible={false} />
            <Column dataField="date5" caption="Date 5" dataType="date" visible={false} />
            <Column dataField="recordType" caption="Detail Type" visible={false} />
            <Column dataField="itemDesc1" caption="Item Desc - 1" visible={false} />
            <Column dataField="itemDesc2" caption="Item Desc - 2" visible={false} />
            <Column dataField="itemType" caption="Item Type" visible={false} />
            <Column dataField="lastUpdateDateTime" caption="Last Updated" dataType="date" visible={false} />
            <Column dataField="lastUpdateUserId" caption="Last Updated By" visible={false} />
            <Column dataField="lotDesc1" caption="Lot Desc - 1" visible={false} />
            <Column dataField="lotDesc2" caption="Lot Desc - 2" visible={false} />
            <Column dataField="module" caption="Module" visible={false} />
            <Column dataField="quantity1" caption="Quantity 1" dataType="number" visible={false} />
            <Column dataField="quantity2" caption="Quantity 2" dataType="number" visible={false} />
            <Column dataField="quantity3" caption="Quantity 3" dataType="number" visible={false} />
            <Column dataField="quantity4" caption="Quantity 4" dataType="number" visible={false} />
            <Column dataField="quantity5" caption="Quantity 5" dataType="number" visible={false} />
            <Column dataField="inventoryQuantity" caption="Quantity Inventory" dataType="number" format={numberFormat} visible={false} />
            <Column dataField="recordNumber" caption="Record #" visible={false} />
            <Column dataField="reference1" caption="Reference 1" visible={false} />
            <Column dataField="reference2" caption="Reference 2" visible={false} />
            <Column dataField="reference3" caption="Reference 3" visible={false} />
            <Column dataField="reference4" caption="Reference 4" visible={false} />
            <Column dataField="reference5" caption="Reference 5" visible={false} />
            <Column dataField="keySequence" caption="Sequence" dataType="number" visible={false} />
            <Toolbar>
                <Item name="exportButton" />
                <Item name="columnChooserButton" />
            </Toolbar>
        </DataGrid>
    );
};

function mapStateToProps(state) {
    return {
        oidc: state.oidc,
    };
}
export default connect(mapStateToProps)(CustomerTransactionDetails);
